/**
 * rateBandsAdmin — the Admin "Rate bands" editor client (rates
 * administration; founder feedback 2026-07-10: the band vocabulary is
 * tenant config, not hardcoded — this makes it visible + editable).
 *
 *   GET /api/t/{slug}/v1/rate-bands   (manage_team)
 *   PUT /api/t/{slug}/v1/rate-bands   (manage_team)
 *
 * `source` says which tier answered: 'platform_default' until the
 * tenant's first save materializes the config row (the editor labels
 * this in plain words). VALUES are load-bearing tokens — removing one
 * that has client_rates, billable_events (in EITHER token column),
 * frozen invoice_line_items rows or frozen invoice_snapshots lines (a
 * void invoice's only remaining carrier) is a fact-shaped 409
 * (rate_band_in_use) listing where it's used. Mirrors
 * src/lib/bandMatrix.ts.
 */

import { writeFetch } from './writeFetch';
import { ApiError } from './api';

export interface RateBandWire {
  value: string;
  label: string;
}

export type RateBandsSourceWire = 'tenant_config' | 'platform_default';

export interface RateBandsSettingsResponse {
  bands: RateBandWire[];
  source: RateBandsSourceWire;
  /** billing.bands_off, sorted: the bands switched off. Absent from an
   *  older answer (a PUT of the list), read as none. */
  bands_off?: string[];
}

/** The 409's facts: one entry per removed-but-in-use band. Byte-mirrors
 *  RateBandUsage in api/_shared/repos/tenantRateBands.ts. */
export interface RateBandUsageWire {
  value: string;
  /** billable_events ROWS carrying the token in rate_label OR rate_band
   *  (0165's two columns) - one row counted once, never a column sum. */
  client_rates_count: number;
  billable_events_count: number;
  /** invoice_line_items rows whose frozen rate_band is the token. */
  invoice_line_items_count: number;
  /** invoice_snapshots documents whose frozen lines carry the token -
   *  per SNAPSHOT, not per line. After a void the snapshot is a token's
   *  only carrier (the void deletes the live line rows), so this can be
   *  the sole non-zero count on a refusal. */
  invoice_snapshots_count: number;
  /** Up to 5 client display names whose rates still use it. */
  clients: string[];
  /** billing.qbo_item_map keys the removal would strand (the band's own
   *  key plus any `<band>:*` compound). Context on the refusal, never a
   *  cause of one - a band with no rows is removed, dangling key and
   *  all. */
  qbo_item_map_keys: string[];
}

/** Admin-console cache namespace (the clientRatesOverviewKey pattern). */
export function rateBandsSettingsKey(slug: string): readonly unknown[] {
  return ['admin-review', 'rate-bands-config', slug];
}

async function parseError(res: Response, label: string): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = await res.text().catch(() => null);
  }
  return new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
}

export async function fetchRateBandsSettings(
  slug: string,
  signal?: AbortSignal,
): Promise<RateBandsSettingsResponse> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/rate-bands`;
  const res = await fetch(path, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw await parseError(res, `GET ${path}`);
  return (await res.json()) as RateBandsSettingsResponse;
}

export async function putRateBands(
  slug: string,
  bands: RateBandWire[],
): Promise<RateBandsSettingsResponse> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/rate-bands`;
  const res = await writeFetch(path, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ bands }),
    redirect: 'manual',
  });
  if (!res.ok) throw await parseError(res, `PUT ${path}`);
  return (await res.json()) as RateBandsSettingsResponse;
}

/** Switch one band on or off (PUT /rate-bands/{band}/switch). Answers
 *  the bands switched off afterwards. */
export async function putBandSwitch(slug: string, band: string, on: boolean): Promise<{ bands_off: string[] }> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/rate-bands/${encodeURIComponent(band)}/switch`;
  const res = await writeFetch(path, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ on }),
    redirect: 'manual',
  });
  if (!res.ok) throw await parseError(res, `PUT ${path}`);
  return (await res.json()) as { bands_off: string[] };
}

/** The 409's in_use facts, or null when the error is something else. */
export function rateBandInUseFacts(err: unknown): RateBandUsageWire[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body;
  if (!body || typeof body !== 'object') return null;
  const inUse = (body as Record<string, unknown>).in_use;
  return Array.isArray(inUse) ? (inUse as RateBandUsageWire[]) : null;
}

/**
 * A value token from a label ("SPICE - Friendly Visitor" ->
 * "spice_friendly_visitor") — the editor's suggestion for a NEW band;
 * the operator can adjust it before the first save (after that it's
 * load-bearing and locked).
 */
export function slugifyBandLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
    .slice(0, 64);
}

/** The editor-side value check (the server re-validates). */
export function isValidBandValue(value: string): boolean {
  return /^[a-z][a-z0-9_]{0,63}$/.test(value);
}
